import {
  CascoCategoryEnum,
  CascoDriver,
  DriverAgeEnum,
  DriverExperienceEnum,
  EditCasco,
  OrderDetail,
  OrderModify,
  OrderStatus,
  PackageEnum,
  RequestCasco
} from '@insurance/api/requests'
import { deviceSession, formatDateToISOFormat } from '@shared/utils'
import { NamePath } from 'antd/es/form/interface'
import dayjs, { Dayjs } from 'dayjs'
import { TFunction } from 'i18next'

import { CascoFormValues } from './type'
import { getCustomerFields, getMixtContractorFields } from '../PersonsFields'

export const INSURANCE_FORM_DATA_KEY = '__casco_insurance_form_data__'
export const MIN_AGE = 18
export const MIN_BONUS = 5
export const MAX_BONUS = 20
export const MAX_ADDITIONAL_EQUIPMENT = 30
export const MAX_INSURANCE_AMOUNT_COEFFICIENT = 1.1
export const MAX_DRIVERS_COUNT = 4

export const saveCascoValuesToStorage = (
  values: CascoFormValues,
  id: string | number = ''
) => {
  const { prevStepKey } = formProps()
  const stringifyValues = JSON.stringify({
    ...values,
    [prevStepKey.name]: undefined
  })

  sessionStorage.setItem(id + INSURANCE_FORM_DATA_KEY, stringifyValues)
}

export const getCascoValuesFromStorage = (id: string | number = '') => {
  const values = sessionStorage.getItem(id + INSURANCE_FORM_DATA_KEY)

  if (!values) return

  const parsedValues = JSON.parse(values)
  const {
    vehicleYearProps,
    vehicleRootProps,
    validFromProps,
    contractorProps,
    driversProps,
    effectiveDateProps
  } = formProps()

  const vehicleYear =
    parsedValues?.[vehicleRootProps.name]?.[vehicleYearProps.name[1]]

  const personDateOfBirth =
    parsedValues[contractorProps.name]?.person?.date_of_birth

  if (personDateOfBirth)
    parsedValues[contractorProps.name].person.date_of_birth =
      dayjs(personDateOfBirth)

  return {
    ...parsedValues,
    [vehicleRootProps.name]: {
      ...parsedValues[vehicleRootProps.name],
      [vehicleYearProps.name[1]]: vehicleYear ? dayjs(vehicleYear) : undefined
    },
    ...(parsedValues[validFromProps.name] && {
      [validFromProps.name]: dayjs(parsedValues[validFromProps.name])
    }),
    [driversProps.name]: parsedValues[driversProps.name]?.map(
      (driver: CascoDriver) => ({
        ...driver,
        ...(driver?.date_of_birth && {
          date_of_birth: dayjs(driver.date_of_birth)
        })
      })
    ),
    [effectiveDateProps.name]: parsedValues[effectiveDateProps.name]
      ? dayjs(parsedValues[effectiveDateProps.name])
      : undefined
  }
}

export const removeCascoValuesFromStorage = (id: string | number = '') =>
  sessionStorage.removeItem(id + INSURANCE_FORM_DATA_KEY)

export const formProps = (tFunction?: TFunction) => {
  const t = tFunction ? tFunction : () => ''

  const vehicleRootName = 'vehicle'
  const additionalEquipmentName = 'additional_equipment'
  const cascoRootName = 'casco'
  const miniRootName = 'mini'

  return {
    contractorProps: {
      name: 'contractor',
      label: t('contractor')
    },
    insurancePackageProps: {
      name: 'package',
      label: t('insurance_package')
    },
    validFromProps: {
      name: 'valid_from',
      label: t('valid_from')
    },
    customerProps: {
      name: 'customer',
      label: t('customer')
    },

    // Vehicle
    vehicleBrandProps: {
      name: [vehicleRootName, 'brand'],
      label: t('brand')
    },
    vehicleModelProps: {
      name: [vehicleRootName, 'model'],
      label: t('model')
    },
    vehicleYearProps: {
      name: [vehicleRootName, 'year'],
      label: t('year')
    },
    vehicleVinProps: {
      name: [vehicleRootName, 'vin'],
      label: t('vin')
    },
    vehicleRegistrationNumberProps: {
      name: [vehicleRootName, 'registration_number'],
      label: t('registration_number')
    },
    vehicleCertificateNumberProps: {
      name: [vehicleRootName, 'certificate_number'],
      label: t('certificate_number')
    },
    vehicleColorProps: {
      name: [vehicleRootName, 'color'],
      label: t('color')
    },
    vehicleFuelProps: {
      name: [vehicleRootName, 'fuel_type'],
      label: t('fuel')
    },
    vehicleTransmissionProps: {
      name: [vehicleRootName, 'transmission_type'],
      label: t('transmission_type')
    },
    vehicleMarketValueProps: {
      name: [vehicleRootName, 'market_value'],
      label: t('market_value')
    },
    vehicleAccessoriesProps: {
      name: [vehicleRootName, 'accessories'],
      label: t('accessories')
    },
    vehicleCategoryProps: {
      name: [vehicleRootName, 'casco_category'],
      label: t('vehicle_category')
    },
    vehicleMileAgeProps: {
      name: [vehicleRootName, 'mileage'],
      label: t('mileage')
    },
    vehicleIsPremium: {
      name: [vehicleRootName, 'brand_is_premium'],
      label: t('brand_is_premium')
    },
    vehicleIsFromChina: {
      name: [vehicleRootName, 'brand_is_china'],
      label: t('brand_is_from_china')
    },
    vehicleSeatsProps: {
      name: [vehicleRootName, 'seats'],
      label: t('seats')
    },
    vehicleEngineCapacityProps: {
      name: [vehicleRootName, 'engine_capacity'],
      label: t('engine_capacity')
    },
    vehicleWeightProps: {
      name: [vehicleRootName, 'weight'],
      label: t('weight')
    },
    vehicleEngineNumberProps: {
      name: [vehicleRootName, 'engine_number'],
      label: t('engine_number')
    },
    vehicleRootProps: {
      name: vehicleRootName,
      label: t('vehicle')
    },
    driversProps: {
      name: 'drivers',
      label: t('drivers')
    },

    // Common CASCO
    cascoRootProps: {
      name: cascoRootName
    },
    insuredAmountProps: {
      name: [cascoRootName, 'amount'],
      label: t('insured_amount')
    },
    driverAgeProps: {
      name: [cascoRootName, 'driver_age'],
      label: t('driver_age')
    },
    driverExperienceProps: {
      name: [cascoRootName, 'driver_experience'],
      label: t('driving_internship')
    },
    franchiseProps: {
      name: [cascoRootName, 'partial_franchise'],
      label: t('deductible')
    },
    territoryProps: {
      name: [cascoRootName, 'territory'],
      label: t('territory')
    },
    parkingModeProps: {
      name: [cascoRootName, 'parking_mode'],
      label: t('parking_mode')
    },
    annualBonusProps: {
      name: [cascoRootName, 'bonus'],
      label: t('annual_bonus')
    },
    vehicleQuantityProps: {
      name: [cascoRootName, 'quantity'],
      label: t('vehicle_quantity')
    },
    claimLastYearProps: {
      name: [cascoRootName, 'claim_last_year'],
      label: t('claim_last_year')
    },
    officialDealerRepairProps: {
      name: [cascoRootName, 'official_dealer_repair'],
      label: t('official_dealer_repair')
    },
    periodProps: {
      name: [cascoRootName, 'period'],
      label: t('period')
    },
    installmentsProps: {
      name: [cascoRootName, 'installments'],
      label: t('installments')
    },
    additionalEquipmentRootProps: {
      name: additionalEquipmentName,
      label: t('additional_equipment')
    },
    additionalEquipmentDescriptionProps: {
      name: [cascoRootName, additionalEquipmentName, 'description'],
      label: t('additional_equipment')
    },
    additionalEquipmentAmountProps: {
      name: [cascoRootName, additionalEquipmentName, 'amount'],
      label: t('additional_equipment_amount')
    },
    fiftyFiftyFullProps: {
      name: [cascoRootName, 'fifty_fifty_full'],
      label: t('fifty_fifty_full')
    },
    costProps: {
      name: 'cost',
      label: t('car_vehicle_price')
    },
    effectiveDateProps: {
      name: 'effective_date',
      label: t('effective_date')
    },

    // Mini CASCO
    cascoMiniRootProps: {
      name: miniRootName
    },
    insuredMiniAmountProps: {
      name: [miniRootName, 'amount'],
      label: t('insured_amount')
    },

    // hidden fields
    refundDestinationKey: {
      name: 'refund_destination'
    },
    stepKey: {
      name: 'step'
    },
    prevStepKey: {
      name: 'prev_step'
    },
    priceKey: {
      name: 'price'
    },
    orderKey: {
      name: 'order_id'
    },
    setBeneficiaryKey: {
      name: 'set_beneficiary_data'
    },
    isOnlineKey: {
      name: 'is_online_order'
    }
  } satisfies Record<string, { name: NamePath; label?: React.ReactNode }>
}

export const checkCascoDrivers = (drivers: (CascoDriver | null)[] = []) => {
  const validDriver = drivers.filter(
    (driver) =>
      driver?.first_name &&
      driver?.last_name &&
      driver?.date_of_birth &&
      driver?.identification_number &&
      driver?.experience
  )

  if (!validDriver.length) return undefined

  return validDriver.map((driver) => ({
    ...driver,
    resident: false,
    country: 'RO',
    date_of_birth: formatDateToISOFormat(driver?.date_of_birth)
  }))
}

export const normalizeCommonData = async (
  values: CascoFormValues,
  useSession = true
) => {
  const session = deviceSession()

  const casco = values?.casco

  const isMini = values?.package === PackageEnum.MINI

  const needToCheckAdditionalEquipment =
    casco?.additional_equipment?.description &&
    casco?.additional_equipment?.amount

  const vehicle = {
    ...values?.vehicle,
    mileage: undefined,
    fuel_type: undefined,
    transmission_type: undefined,
    seats: undefined,
    engine_capacity: undefined,
    engine_number: undefined,
    ...(enableEngineFields(values.vehicle?.casco_category) && values.vehicle),
    year: Number(dayjs(values?.vehicle?.year).format('YYYY'))
  }

  return {
    ...values,
    step: undefined,
    prev_step: undefined,
    price: undefined,
    order_id: undefined,
    refund_destination: undefined,
    effective_date: undefined,
    drivers: checkCascoDrivers(values.drivers),

    ...((!isMini
      ? {
          mini: undefined,
          casco: {
            claim_last_year: false,
            official_dealer_repair: false,
            ...casco,
            additional_equipment: undefined,
            partial_franchise: undefined,
            ...(enableFranchise(
              values?.vehicle?.casco_category,
              values.package
            ) && {
              partial_franchise: casco?.partial_franchise
            }),
            ...(needToCheckAdditionalEquipment && {
              additional_equipment: casco?.additional_equipment
            })
          }
        }
      : { casco: undefined, mini: values.mini }) as RequestCasco),

    valid_from: formatDateToISOFormat(values.valid_from),

    vehicle: isMini ? omitEmptyMiniVehicleFields(vehicle) : vehicle,

    ...getMixtContractorFields(values),

    ...(useSession && {
      session: {
        device_id: session.deviceId,
        session_id: session.sessionId
      }
    })
  } as EditCasco
}

export const normalizeOrderData = async (values: CascoFormValues) => {
  const prevValues = await normalizeCommonData(values, false)

  return {
    ...prevValues,
    customer: getCustomerFields(values.customer)
  } as EditCasco
}

export const normalizeModifyOrderData = async (
  values: CascoFormValues
): Promise<OrderModify> => {
  const data = await normalizeOrderData(values)

  return {
    casco: {
      effective_date: formatDateToISOFormat(values.effective_date)!,
      refund_destination: values.refund_destination,
      cost: data.cost,
      package: data.package,
      contractor: data.contractor,
      vehicle: data.vehicle,
      customer: data.customer,
      drivers: data.drivers,
      beneficiary: data.beneficiary,
      insured_person: data.insured_person,
      insured_same_as_contractor: data.insured_same_as_contractor,
      ...(data.casco && {
        casco: {
          amount: data.casco.amount,
          territory: data.casco.territory,
          additional_equipment: data.casco.additional_equipment,
          driver_age: data.casco.driver_age,
          driver_experience: data.casco.driver_experience,
          fifty_fifty_full: data.casco.fifty_fifty_full
        }
      }),
      ...(data.mini && {
        mini: {
          amount: data.mini.amount
        }
      })
    }
  } as OrderModify
}

export const enableFranchise = (
  vehicleCategory?: CascoCategoryEnum,
  insurancePackage?: PackageEnum
) =>
  vehicleCategory &&
  Object.values(CascoCategoryEnum).includes(vehicleCategory) &&
  insurancePackage &&
  [PackageEnum.STANDARD, PackageEnum.FIFTY_FIFTY, PackageEnum.COMFORT].includes(
    insurancePackage
  )

export const enableEngineFields = (vehicleCategory?: CascoCategoryEnum) =>
  vehicleCategory !== CascoCategoryEnum.TRAILER

// For casco mini these vehicle fields must be omitted (not sent as null) when
// they have no value, so the payload doesn't carry empty keys.
export const MINI_OPTIONAL_VEHICLE_FIELDS = [
  'color',
  'fuel_type',
  'transmission_type',
  'mileage',
  'seats',
  'engine_number',
  'engine_capacity',
  'weight'
] as const

export const omitEmptyMiniVehicleFields = <T extends Record<string, unknown>>(
  vehicle: T
): T => {
  const result = { ...vehicle }

  MINI_OPTIONAL_VEHICLE_FIELDS.forEach((field) => {
    const value = result[field]

    if (value === null || value === undefined || value === '')
      delete result[field]
  })

  return result
}

export const vehicleMaxYear: Partial<Record<PackageEnum, Dayjs>> = {
  [PackageEnum.STANDARD]: dayjs().subtract(15, 'year'),
  [PackageEnum.FIFTY_FIFTY]: dayjs().subtract(15, 'year'),
  [PackageEnum.COMFORT]: dayjs().subtract(15, 'year'),
  [PackageEnum.MINI]: dayjs().subtract(16, 'year')
}

export const onlyVehicle = (insurancePackage?: PackageEnum) =>
  insurancePackage &&
  [PackageEnum.MINI, PackageEnum.COMFORT].includes(insurancePackage)

export const rangeDriverAge = (age: number) =>
  age < 23 ? DriverAgeEnum.LT_23 : DriverAgeEnum.GTE_23

export const rangeDriverExperience = (experience: number) =>
  experience <= 2 ? DriverExperienceEnum.LT_2 : DriverExperienceEnum.GTE_2

export const orderParentOrChildId = (order?: OrderDetail) =>
  !!order?.status &&
  [OrderStatus.COMPLETED, OrderStatus.PARTIAL_PAID].includes(order.status)
